/**
 * SmartEditorManager
 *
 * Manages state for SmartEditor instances:
 * - Input value and mentioned files
 * - Draft and image attachment state
 *
 * Managers are retrieved by ID and workspaceId via SmartEditorManagerStore.
 */

import { makeAutoObservable, observable, runInAction } from "mobx"
import { ulid } from "ulid"
import type { ImageAttachment, UserInputContext } from "../../types"
import { loadImageBlob } from "../../utils/imageAttachment"

const ACTIVE_DRAFT_SAVE_DELAY_MS = 500

// ============================================================================
// Types
// ============================================================================

export interface EditorSnapshot {
    value: string
    files: string[]
    editorContent: Record<string, unknown> | null
    pendingImages: ImageAttachment[]
    pendingImageDataUrls: Map<string, string>
}

export interface StashedDraft {
    id: string
    createdAt: string
    snapshot: EditorSnapshot
}

interface PersistedEditorSnapshot {
    value: string
    files: string[]
    editorContent: Record<string, unknown> | null
    pendingImages: ImageAttachment[]
}

interface PersistedStashedDraft {
    id: string
    createdAt: string
    snapshot: PersistedEditorSnapshot
}

interface PersistedEditorDraft {
    v: 1
    snapshot: PersistedEditorSnapshot
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value)
}

function parseImageAttachment(value: unknown): ImageAttachment | null {
    if (
        !isRecord(value) ||
        typeof value.id !== "string" ||
        typeof value.mediaType !== "string" ||
        typeof value.ext !== "string" ||
        typeof value.originalWidth !== "number" ||
        typeof value.originalHeight !== "number" ||
        typeof value.resizedWidth !== "number" ||
        typeof value.resizedHeight !== "number" ||
        (value.attachmentId !== undefined && typeof value.attachmentId !== "string")
    ) {
        return null
    }

    return {
        id: value.id,
        ...(value.attachmentId === undefined ? {} : { attachmentId: value.attachmentId }),
        mediaType: value.mediaType,
        ext: value.ext,
        originalWidth: value.originalWidth,
        originalHeight: value.originalHeight,
        resizedWidth: value.resizedWidth,
        resizedHeight: value.resizedHeight,
    }
}

function parsePersistedEditorSnapshot(value: unknown): PersistedEditorSnapshot | null {
    if (!isRecord(value) || typeof value.value !== "string") return null

    return {
        value: value.value,
        files: Array.isArray(value.files) ? value.files.filter((file): file is string => typeof file === "string") : [],
        editorContent: value.editorContent === null || isRecord(value.editorContent) ? value.editorContent : null,
        pendingImages: Array.isArray(value.pendingImages)
            ? value.pendingImages.map(parseImageAttachment).filter((image): image is ImageAttachment => image !== null)
            : [],
    }
}

function parsePersistedEditorDraft(value: unknown): PersistedEditorDraft | null {
    if (!isRecord(value) || value.v !== 1) return null
    const snapshot = parsePersistedEditorSnapshot(value.snapshot)
    return snapshot ? { v: 1, snapshot } : null
}

function cloneEditorContent(content: Record<string, unknown> | null): Record<string, unknown> | null {
    if (content === null) return null
    return JSON.parse(JSON.stringify(content)) as Record<string, unknown>
}

function clonePendingImages(images: ImageAttachment[]): ImageAttachment[] {
    return images.map((image) => ({ ...image }))
}

function paragraphsForText(text: string): Array<Record<string, unknown>> {
    return text.split("\n").map((line) => ({
        type: "paragraph",
        ...(line ? { content: [{ type: "text", text: line }] } : {}),
    }))
}

function editorContentNodes(content: Record<string, unknown> | null, fallbackText: string): unknown[] {
    if (content?.type === "doc" && Array.isArray(content.content)) return content.content
    return fallbackText ? paragraphsForText(fallbackText) : []
}

function prependEditorContent(prefix: string, currentText: string, currentContent: Record<string, unknown> | null): Record<string, unknown> {
    const prefixNodes = prefix ? paragraphsForText(prefix) : []
    const currentNodes = currentText ? editorContentNodes(currentContent, currentText) : []
    return {
        type: "doc",
        content: [...prefixNodes, ...(prefixNodes.length > 0 && currentNodes.length > 0 ? [{ type: "paragraph" }] : []), ...currentNodes],
    }
}

async function loadImagePreviewUrl(image: ImageAttachment): Promise<string | null> {
    try {
        const blob = await loadImageBlob(image)
        if (!blob) return null
        return URL.createObjectURL(blob)
    } catch (err) {
        console.error("[SmartEditorManager] Failed to restore image preview:", image.id, err)
        return null
    }
}

// ============================================================================
// SmartEditorManager
// ============================================================================

export class SmartEditorManager {
    readonly id: string
    readonly workspaceId: string

    value = ""
    files: string[] = [] // Currently mentioned files in editor
    // TipTap JSON content - source of truth for editor state
    editorContent: Record<string, unknown> | null = null
    contentVersion = 0
    // Pending image attachments (not yet submitted)
    pendingImages: ImageAttachment[] = []
    // In-memory data URLs for previewing pending images (keyed by image ID)
    pendingImageDataUrls: Map<string, string> = observable.map()
    stashedDrafts: StashedDraft[] = []

    // Callbacks registered by SmartEditor
    private onClear: (() => void) | null = null
    private onSetContent: ((content: string | Record<string, unknown> | null) => void) | null = null
    private persistActiveDraft = false
    private activeDraftSaveTimer: ReturnType<typeof setTimeout> | null = null
    private activeDraftFlushListenersRegistered = false
    private readonly flushActiveDraftOnPageHide = (): void => {
        this.flushActiveDraftSave()
    }
    private readonly flushActiveDraftWhenHidden = (): void => {
        if (document.visibilityState === "hidden") this.flushActiveDraftSave()
    }

    constructor(id: string, workspaceId: string) {
        this.id = id
        this.workspaceId = workspaceId
        makeAutoObservable<
            this,
            | "onClear"
            | "onSetContent"
            | "persistActiveDraft"
            | "activeDraftSaveTimer"
            | "activeDraftFlushListenersRegistered"
            | "flushActiveDraftOnPageHide"
            | "flushActiveDraftWhenHidden"
        >(this, {
            files: false,
            editorContent: false,
            onClear: false,
            onSetContent: false,
            persistActiveDraft: false,
            activeDraftSaveTimer: false,
            activeDraftFlushListenersRegistered: false,
            flushActiveDraftOnPageHide: false,
            flushActiveDraftWhenHidden: false,
        })
        void this.loadPersistedStashes()
    }

    // === Input state ===

    setValue(value: string): void {
        this.value = value
        this.contentVersion++
        this.scheduleActiveDraftSave()
    }

    setFiles(files: string[]): void {
        this.files = files
        this.contentVersion++
        this.scheduleActiveDraftSave()
    }

    setEditorContent(content: Record<string, unknown> | null): void {
        this.editorContent = content
        this.contentVersion++
        this.scheduleActiveDraftSave()
    }

    syncFromEditor(update: { value: string; files: string[]; editorContent: Record<string, unknown> }): void {
        this.value = update.value
        this.files = update.files
        this.editorContent = update.editorContent
        this.contentVersion++
        this.scheduleActiveDraftSave()
    }

    get hasDraftableContent(): boolean {
        return this.value.trim().length > 0 || this.pendingImages.length > 0
    }

    get hasText(): boolean {
        return this.value.trim().length > 0
    }

    clear(options?: { revokeImagePreviews?: boolean }): void {
        const revokeImagePreviews = options?.revokeImagePreviews ?? true
        this.value = ""
        this.files = []
        this.editorContent = null
        this.contentVersion++
        if (revokeImagePreviews) {
            for (const url of this.pendingImageDataUrls.values()) {
                URL.revokeObjectURL(url)
            }
        }
        this.pendingImages = []
        this.pendingImageDataUrls.clear()
        if (this.onClear) {
            this.onClear()
        }
        this.removeSavedActiveDraft()
    }

    captureSnapshot(): EditorSnapshot {
        return {
            value: this.value,
            files: [...this.files],
            editorContent: cloneEditorContent(this.editorContent),
            pendingImages: clonePendingImages(this.pendingImages),
            pendingImageDataUrls: new Map(this.pendingImageDataUrls),
        }
    }

    restoreSnapshot(snapshot: EditorSnapshot): void {
        this.value = snapshot.value
        this.files = [...snapshot.files]
        this.editorContent = cloneEditorContent(snapshot.editorContent)
        this.contentVersion++
        this.pendingImages = clonePendingImages(snapshot.pendingImages)
        this.pendingImageDataUrls.clear()
        for (const [id, dataUrl] of snapshot.pendingImageDataUrls.entries()) {
            this.pendingImageDataUrls.set(id, dataUrl)
        }
        this.scheduleActiveDraftSave()
        this.onSetContent?.(this.editorContent)
    }

    async restoreMissingImagePreviews(): Promise<void> {
        const missingImages = this.pendingImages.filter((image) => !this.pendingImageDataUrls.has(image.id))
        if (missingImages.length === 0) return

        const previewUrls = await this.createPreviewUrlMap(missingImages)
        runInAction(() => {
            const pendingImageIds = new Set(this.pendingImages.map((image) => image.id))
            for (const [id, url] of previewUrls) {
                if (pendingImageIds.has(id) && !this.pendingImageDataUrls.has(id)) this.pendingImageDataUrls.set(id, url)
                else URL.revokeObjectURL(url)
            }
        })
    }

    async restoreCancelledInput(input: UserInputContext): Promise<void> {
        const current = this.captureSnapshot()
        const restoredText = input.userInput.trim()
        const currentText = current.value.trim()
        const currentImageIds = new Set(current.pendingImages.map((image) => image.id))
        const restoredImages: ImageAttachment[] = []

        for (const image of input.images) {
            if (currentImageIds.has(image.id)) continue
            currentImageIds.add(image.id)
            restoredImages.push(image)
        }

        this.restoreSnapshot({
            value: [restoredText, currentText].filter(Boolean).join("\n\n"),
            files: current.files,
            editorContent: prependEditorContent(restoredText, currentText, current.editorContent),
            pendingImages: [...restoredImages, ...current.pendingImages],
            pendingImageDataUrls: current.pendingImageDataUrls,
        })

        const restoredPreviewUrls = await this.createPreviewUrlMap(restoredImages)
        runInAction(() => {
            const pendingImageIds = new Set(this.pendingImages.map((image) => image.id))
            for (const [id, url] of restoredPreviewUrls) {
                if (pendingImageIds.has(id)) this.pendingImageDataUrls.set(id, url)
                else URL.revokeObjectURL(url)
            }
        })
    }

    stashCurrentDraft(): StashedDraft | null {
        if (!this.hasDraftableContent) return null

        const draft = this.createDraft(this.captureSnapshot())
        this.stashedDrafts.unshift(draft)
        this.persistStashedDrafts()
        this.clear({ revokeImagePreviews: false })
        return draft
    }

    popStash(stashId?: string): boolean {
        const nextDraft = stashId ? this.stashedDrafts.find((draft) => draft.id === stashId) : this.stashedDrafts[0]
        if (!nextDraft) return false

        this.stashedDrafts = this.stashedDrafts.filter((draft) => draft.id !== nextDraft.id)
        if (this.hasDraftableContent) {
            this.stashedDrafts.unshift(this.createDraft(this.captureSnapshot()))
        }

        this.persistStashedDrafts()
        this.restoreSnapshot(nextDraft.snapshot)
        return true
    }

    deleteStash(stashId: string): void {
        const draft = this.stashedDrafts.find((item) => item.id === stashId)
        if (!draft) return

        for (const url of draft.snapshot.pendingImageDataUrls.values()) {
            URL.revokeObjectURL(url)
        }
        this.stashedDrafts = this.stashedDrafts.filter((item) => item.id !== stashId)
        this.persistStashedDrafts()
    }

    addImage(image: ImageAttachment, dataUrl: string): void {
        this.pendingImages.push(image)
        this.pendingImageDataUrls.set(image.id, dataUrl)
        this.scheduleActiveDraftSave()
    }

    removeImage(id: string): void {
        const url = this.pendingImageDataUrls.get(id)
        if (url) URL.revokeObjectURL(url)
        this.pendingImages = this.pendingImages.filter((img) => img.id !== id)
        this.pendingImageDataUrls.delete(id)
        this.scheduleActiveDraftSave()
    }

    // === Editor callbacks (registered by SmartEditor) ===

    registerClearCallback(cb: () => void): void {
        this.onClear = cb
    }

    unregisterClearCallback(): void {
        this.onClear = null
    }

    registerSetContentCallback(cb: (content: string | Record<string, unknown> | null) => void): void {
        this.onSetContent = cb
    }

    unregisterSetContentCallback(): void {
        this.onSetContent = null
    }

    /** Programmatically set the editor text content (replaces everything) */
    setTextContent(text: string): void {
        this.value = text
        this.contentVersion++
        this.scheduleActiveDraftSave()
        if (this.onSetContent) {
            this.onSetContent(text)
        }
    }

    enableActiveDraftPersistence(): void {
        if (this.persistActiveDraft) return
        this.persistActiveDraft = true
        void this.loadActiveDraft()
    }

    private createDraft(snapshot: EditorSnapshot): StashedDraft {
        return {
            id: ulid(),
            createdAt: new Date().toISOString(),
            snapshot,
        }
    }

    private async loadPersistedStashes(): Promise<void> {
        const persistedDrafts = this.getPersistedStashedDrafts()
        if (persistedDrafts.length === 0) return

        const drafts = await Promise.all(
            persistedDrafts.map(async (draft) => ({
                id: draft.id,
                createdAt: draft.createdAt,
                snapshot: {
                    value: draft.snapshot.value,
                    files: [...draft.snapshot.files],
                    editorContent: cloneEditorContent(draft.snapshot.editorContent),
                    pendingImages: clonePendingImages(draft.snapshot.pendingImages),
                    pendingImageDataUrls: await this.createPreviewUrlMap(draft.snapshot.pendingImages),
                },
            }))
        )

        runInAction(() => {
            this.stashedDrafts = drafts
        })
    }

    private async loadActiveDraft(): Promise<void> {
        const persistedDraft = this.getPersistedActiveDraft()
        if (!persistedDraft) return

        this.value = persistedDraft.snapshot.value
        this.files = [...persistedDraft.snapshot.files]
        this.editorContent = cloneEditorContent(persistedDraft.snapshot.editorContent)
        this.contentVersion++
        this.pendingImages = clonePendingImages(persistedDraft.snapshot.pendingImages)

        const previewUrls = await this.createPreviewUrlMap(persistedDraft.snapshot.pendingImages)
        runInAction(() => {
            const pendingImageIds = new Set(this.pendingImages.map((image) => image.id))
            for (const [id, url] of previewUrls) {
                if (pendingImageIds.has(id)) this.pendingImageDataUrls.set(id, url)
                else URL.revokeObjectURL(url)
            }
        })
    }

    private getPersistedActiveDraft(): PersistedEditorDraft | null {
        try {
            const data = localStorage.getItem(this.getActiveDraftKey())
            return data ? parsePersistedEditorDraft(JSON.parse(data)) : null
        } catch {
            return null
        }
    }

    private scheduleActiveDraftSave(): void {
        if (!this.persistActiveDraft) return

        if (this.activeDraftSaveTimer !== null) {
            clearTimeout(this.activeDraftSaveTimer)
        } else {
            this.registerActiveDraftFlushListeners()
        }

        this.activeDraftSaveTimer = setTimeout(() => this.flushActiveDraftSave(), ACTIVE_DRAFT_SAVE_DELAY_MS)
    }

    private flushActiveDraftSave(): void {
        if (this.activeDraftSaveTimer === null) return

        clearTimeout(this.activeDraftSaveTimer)
        this.activeDraftSaveTimer = null
        this.unregisterActiveDraftFlushListeners()
        this.saveActiveDraftNow()
    }

    private saveActiveDraftNow(): void {
        if (!this.persistActiveDraft) return

        const key = this.getActiveDraftKey()
        try {
            if (!this.value && this.pendingImages.length === 0) {
                localStorage.removeItem(key)
                return
            }

            const draft: PersistedEditorDraft = {
                v: 1,
                snapshot: {
                    value: this.value,
                    files: [...this.files],
                    editorContent: cloneEditorContent(this.editorContent),
                    pendingImages: clonePendingImages(this.pendingImages),
                },
            }
            localStorage.setItem(key, JSON.stringify(draft))
        } catch (err) {
            console.error("[SmartEditorManager] Failed to persist active draft:", err)
        }
    }

    private removeSavedActiveDraft(): void {
        if (!this.persistActiveDraft) return

        if (this.activeDraftSaveTimer !== null) {
            clearTimeout(this.activeDraftSaveTimer)
            this.activeDraftSaveTimer = null
        }
        this.unregisterActiveDraftFlushListeners()

        try {
            localStorage.removeItem(this.getActiveDraftKey())
        } catch (err) {
            console.error("[SmartEditorManager] Failed to remove active draft:", err)
        }
    }

    private registerActiveDraftFlushListeners(): void {
        if (this.activeDraftFlushListenersRegistered) return
        this.activeDraftFlushListenersRegistered = true
        window.addEventListener("pagehide", this.flushActiveDraftOnPageHide)
        document.addEventListener("visibilitychange", this.flushActiveDraftWhenHidden)
    }

    private unregisterActiveDraftFlushListeners(): void {
        if (!this.activeDraftFlushListenersRegistered) return
        this.activeDraftFlushListenersRegistered = false
        window.removeEventListener("pagehide", this.flushActiveDraftOnPageHide)
        document.removeEventListener("visibilitychange", this.flushActiveDraftWhenHidden)
    }

    private getActiveDraftKey(): string {
        return `code:editorDraft:${this.workspaceId}:${this.id}`
    }

    private getPersistedStashedDrafts(): PersistedStashedDraft[] {
        try {
            const data = localStorage.getItem(this.getStashedDraftsKey())
            if (!data) return []
            const parsed = JSON.parse(data)
            if (!Array.isArray(parsed)) return []
            return parsed as PersistedStashedDraft[]
        } catch {
            return []
        }
    }

    private persistStashedDrafts(): void {
        if (this.stashedDrafts.length === 0) {
            localStorage.removeItem(this.getStashedDraftsKey())
            return
        }

        const serialized: PersistedStashedDraft[] = this.stashedDrafts.map((draft) => ({
            id: draft.id,
            createdAt: draft.createdAt,
            snapshot: {
                value: draft.snapshot.value,
                files: [...draft.snapshot.files],
                editorContent: cloneEditorContent(draft.snapshot.editorContent),
                pendingImages: clonePendingImages(draft.snapshot.pendingImages),
            },
        }))

        localStorage.setItem(this.getStashedDraftsKey(), JSON.stringify(serialized))
    }

    private getStashedDraftsKey(): string {
        return `code:stashedDrafts:${this.workspaceId}:${this.id}`
    }

    private async createPreviewUrlMap(images: ImageAttachment[]): Promise<Map<string, string>> {
        const entries = await Promise.all(
            images.map(async (image) => {
                const url = await loadImagePreviewUrl(image)
                return url ? ([image.id, url] as const) : null
            })
        )

        return new Map(entries.filter((entry): entry is readonly [string, string] => entry !== null))
    }
}

// ============================================================================
// SmartEditorManagerStore
// ============================================================================

export class SmartEditorManagerStore {
    private managers = new Map<string, SmartEditorManager>()

    constructor() {
        makeAutoObservable(this)
    }

    getManager(id: string, workspaceId: string): SmartEditorManager {
        const key = `${workspaceId}:${id}`
        const existing = this.managers.get(key)
        if (existing) return existing

        const manager = new SmartEditorManager(id, workspaceId)
        if (id.startsWith("task-")) manager.enableActiveDraftPersistence()
        this.managers.set(key, manager)
        return manager
    }

    swapDrafts(id: string, firstWorkspaceId: string, secondWorkspaceId: string): void {
        if (firstWorkspaceId === secondWorkspaceId) return

        const first = this.getManager(id, firstWorkspaceId)
        if (!first.hasDraftableContent && first.files.length === 0) return

        const second = this.getManager(id, secondWorkspaceId)
        const firstSnapshot = first.captureSnapshot()
        const secondSnapshot = second.captureSnapshot()

        first.restoreSnapshot(secondSnapshot)
        second.restoreSnapshot(firstSnapshot)
        void first.restoreMissingImagePreviews()
        void second.restoreMissingImagePreviews()
    }

    disposeManager(id: string, workspaceId: string): void {
        const key = `${workspaceId}:${id}`
        this.managers.delete(key)
    }
}
